import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { FilterPreferences } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useListLayoutPreferences } from '../../hooks/useListLayoutPreferences';
import { useFilterPreferences } from '../../hooks/useFilterPreferences';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';
import RichTextEditor from '../../components/RichTextEditor';
import EmailTicketModal from '../../components/EmailTicketModal';
import ListLayoutSettingsModal from '../../components/ListLayoutSettingsModal';
import ListLayoutSettingsButton from '../../components/ListLayoutSettingsButton';
import RefreshButton from '../../components/RefreshButton';
import { sanitizeNoteInput, hasActualTextContent } from '../../utils/sanitize';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import TableColumnFilter from '../../components/TableColumnFilter';
import GenericExportButton from '../../components/GenericExportButton';
import { ExportConfigs } from '../../services/genericExportService';
import { trackCaseOperation } from '../../utils/appInsights';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { isTicketSearchInputValid } from '../../utils/searchTerms';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ComputerDesktopIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface Case {
  id?: string;
  _id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: string;
  state: string;
  category: string;
  caseType?: string;
  channel: string;
  impact: string;
  urgency: string;
  relatedRecordsCount?: number;
  canEdit?: boolean;
  caller?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  contact?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedTo?: {
    _id?: string;
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  AssignmentGroup?: {
    id: string;
    name: string;
  };
  Customer?: {
    id: string;
    accountName: string;
    accountShortName?: string;
    sbu?: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  ServicePlan?: {
    id: string;
    name: string;
  };
  Resource?: {
    id: string;
    name: string;
  };
  resourceIdValue?: string;
  slaBreached?: boolean;
  slaBreachTime?: string;
  slaStatus?: Array<{
    targetType: string;
    currentState: string;
    isBreached: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
  createdById?: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const priorityColors = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityLabels = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning'
};



const channelIcons = {
  web: <ComputerDesktopIcon className="h-5 w-5 text-gray-500" />,
  phone: <PhoneIcon className="h-5 w-5 text-gray-500" />,
  email: <EnvelopeIcon className="h-5 w-5 text-gray-500" />,
  alert: <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-500" />
};

const stateColors = {
  new: 'bg-blue-100 text-blue-800',
  open: 'bg-indigo-100 text-indigo-800',
  awaiting_info: 'bg-purple-100 text-purple-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  solution_proposed: 'bg-teal-100 text-teal-800',
  closed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  in_progress: 'bg-green-100 text-green-800',
  auto_resolved: 'bg-emerald-100 text-emerald-800'
};

const stateLabels = {
  new: 'New',
  open: 'Open',
  awaiting_info: 'Awaiting Info',
  on_hold: 'On Hold',
  solution_proposed: 'Solution Proposed',
  closed: 'Closed',
  cancelled: 'Cancelled',
  in_progress: 'In Progress',
  auto_resolved: 'Auto Resolved'
};

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// SLA Status display component with icons and tooltips
interface SlaStatusItem {
  targetType: string;
  currentState: string;
  isBreached: boolean;
}

const SlaStatusDisplay: React.FC<{ slaStatus?: SlaStatusItem[] }> = ({ slaStatus }) => {
  if (!slaStatus || slaStatus.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  const getStatusIcon = (item: SlaStatusItem) => {
    if (item.isBreached || item.currentState === 'BREACHED') {
      return '🔴';
    }
    if (item.currentState === 'ACHIEVED' || item.currentState === 'COMPLETED') {
      return '✅';
    }
    if (item.currentState === 'RUNNING') {
      return '⏳';
    }
    if (item.currentState === 'PAUSED') {
      return '⏸️';
    }
    if (item.currentState === 'CANCELLED') {
      return '⚪';
    }
    return '⏳';
  };

  const getTargetLabel = (targetType: string) => {
    switch (targetType) {
      case 'RESPONSE': return 'R';
      case 'ONGOING': return 'O';
      case 'RESOLUTION': return 'S';
      default: return targetType.charAt(0);
    }
  };

  const getFullTargetName = (targetType: string) => {
    switch (targetType) {
      case 'RESPONSE': return 'Response SLA';
      case 'ONGOING': return 'Ongoing SLA';
      case 'RESOLUTION': return 'Resolution SLA';
      default: return targetType;
    }
  };

  const getStatusText = (item: SlaStatusItem) => {
    if (item.isBreached || item.currentState === 'BREACHED') return 'Breached';
    if (item.currentState === 'ACHIEVED' || item.currentState === 'COMPLETED') return 'Met';
    if (item.currentState === 'RUNNING') return 'Running';
    if (item.currentState === 'PAUSED') return 'Paused';
    if (item.currentState === 'CANCELLED') return 'Cancelled';
    return item.currentState;
  };

  // Sort by target type order: RESPONSE, ONGOING, RESOLUTION
  const sortOrder = { 'RESPONSE': 1, 'ONGOING': 2, 'RESOLUTION': 3 };
  const sortedStatus = [...slaStatus].sort((a, b) =>
    (sortOrder[a.targetType as keyof typeof sortOrder] || 99) -
    (sortOrder[b.targetType as keyof typeof sortOrder] || 99)
  );

  return (
    <div className="flex items-center gap-1">
      {sortedStatus.map((item, index) => (
        <div
          key={item.targetType}
          className="relative group inline-flex items-center"
        >
          <span className="cursor-help text-xs font-medium text-gray-600">
            {getTargetLabel(item.targetType)}
          </span>
          <span className="text-sm ml-0.5">{getStatusIcon(item)}</span>
          {index < sortedStatus.length - 1 && <span className="text-gray-300 mx-0.5">|</span>}
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            {getFullTargetName(item.targetType)}: {getStatusText(item)}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Truncated text with tooltip component
const TruncatedTextWithTooltip: React.FC<{ text: string | null | undefined; maxLength?: number }> = ({ text, maxLength = 50 }) => {
  if (!text) {
    return <span className="text-gray-400">-</span>;
  }

  const strippedText = stripHtml(text);
  const firstLine = strippedText.split('\n')[0];
  const displayText = firstLine.length > maxLength ? firstLine.substring(0, maxLength) + '...' : firstLine;
  const needsTooltip = strippedText.length > maxLength || strippedText.includes('\n');

  if (!needsTooltip) {
    return <span>{displayText}</span>;
  }

  return (
    <div className="relative group">
      <span className="cursor-help">{displayText}</span>
      <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded max-w-md whitespace-pre-wrap break-all opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
        {strippedText}
        <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
};

// Options for multi-select filters
const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'awaiting_info', label: 'Awaiting Info' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'solution_proposed', label: 'Solution Proposed' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'auto_resolved', label: 'Auto Resolved' },
];

const priorityOptions = [
  { value: 'critical_1', label: 'Critical' },
  { value: 'high_2', label: 'High' },
  { value: 'moderate_3', label: 'Moderate' },
  { value: 'low_4', label: 'Low' },
  { value: 'planning_5', label: 'Planning' },
];

const timeRangeOptions = [
  { value: '5', label: 'Last 5 mins' },
  { value: '15', label: 'Last 15 mins' },
  { value: '30', label: 'Last 30 mins' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const impactOptions = [
  { value: 'high_1', label: '1 - High' },
  { value: 'medium_2', label: '2 - Medium' },
  { value: 'low_3', label: '3 - Low' },
];

const urgencyOptions = [
  { value: 'high_1', label: '1 - High' },
  { value: 'medium_2', label: '2 - Medium' },
  { value: 'low_3', label: '3 - Low' },
];

const categoryOptions = [
  { value: 'question', label: 'Question' },
  { value: 'issue', label: 'Issue' },
  { value: 'request', label: 'Request' },
  { value: 'change', label: 'Change' },
];

const channelOptions = [
  { value: 'web', label: 'Web' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'alert', label: 'Alert' },
];

const CASE_LIST_URL_FILTER_KEYS = [
  'search',
  'number',
  'desc',
  'contact',
  'state',
  'priority',
  'time',
  'customer',
  'engagement',
  'alert',
  'groups',
  'assignedToMe',
  'assignedTo',
  'unassigned',
  'category',
  'channel',
  'impact',
  'urgency',
  'createdBy',
  'sla',
] as const;

function isDefaultCaseListUrl(sp: URLSearchParams): boolean {
  const page = sp.get('page');
  if (page && page !== '1') return false;
  const ps = sp.get('pageSize');
  if (ps && ps !== '25') return false;
  for (const k of CASE_LIST_URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== '') return false;
  }
  return true;
}

function buildCaseListUrlUpdatesFromSaved(
  savedFilters: FilterPreferences
): Record<string, string | null | undefined> {
  const updates: Record<string, string | null | undefined> = {};
  if (savedFilters.state?.length) updates.state = savedFilters.state.join(',');
  if (savedFilters.priority?.length) updates.priority = savedFilters.priority.join(',');
  if (savedFilters.timeRange === '') {
    updates.time = 'all';
  } else if (savedFilters.timeRange && savedFilters.timeRange !== 'preset:this_month') {
    updates.time = savedFilters.timeRange;
  }
  if (savedFilters.customer) updates.customer = savedFilters.customer;
  if (savedFilters.engagement) {
    updates.engagement = savedFilters.engagement;
  }
  if (savedFilters.assignmentGroup) {
    updates.groups = savedFilters.assignmentGroup;
  }
  if (savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null) {
    updates.assignedToMe =
      savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false';
  }
  if (savedFilters.assignedTo) updates.assignedTo = savedFilters.assignedTo;
  if (savedFilters.unassigned) updates.unassigned = savedFilters.unassigned;
  if (savedFilters.category) updates.category = savedFilters.category;
  if (savedFilters.channel) updates.channel = savedFilters.channel;
  return updates;
}

function serializeCaseTimeRangeForUrl(timeRange: string): string | null {
  if (!timeRange) return 'all';
  if (timeRange === 'preset:this_month') return null;
  return timeRange;
}

const CaseList: React.FC = () => {
  // Subscribe to real-time case events via SSE
  useTicketEvents({ ticketTypes: ['CASE'] });

  const { isContact, user } = useAuth();
  const { can } = useDynamicPermissions();
  const hasUpdatePermission = can('cases', 'update');
  const isContactUser = isContact();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/cases', LIST_FILTER_SESSION_KEYS.cases);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';
  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const filterState = useMemo(
    () => (searchParams.get('state') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterCategory = searchParams.get('category') ?? '';
  const filterChannel = searchParams.get('channel') ?? '';
  const filterPriority = useMemo(
    () => (searchParams.get('priority') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterTimeRange = useMemo(() => {
    const t = searchParams.get('time');
    if (t === 'all') return '';
    if (t === null || t === '') return 'preset:this_month';
    return t;
  }, [searchParams]);

  const filterCustomer = searchParams.get('customer') ?? '';
  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const appliedAlertNumber = searchParams.get('alert') ?? '';
  const [filterAlertNumber, setFilterAlertNumber] = useState(() => searchParams.get('alert') ?? '');
  useEffect(() => {
    setFilterAlertNumber(appliedAlertNumber);
  }, [appliedAlertNumber]);

  const filterAssignmentGroup = useMemo(
    () => (searchParams.get('groups') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterAssignedToMe = searchParams.get('assignedToMe') ?? '';
  const filterAssignedTo = searchParams.get('assignedTo') ?? '';
  const filterUnassigned = searchParams.get('unassigned') ?? '';
  const filterNumber = searchParams.get('number') ?? '';
  const filterDescription = searchParams.get('desc') ?? '';
  const filterContact = searchParams.get('contact') ?? '';
  const filterCreatedBy = searchParams.get('createdBy') ?? '';
  const filterImpact = useMemo(
    () => (searchParams.get('impact') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterUrgency = useMemo(
    () => (searchParams.get('urgency') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterSlaBreached = searchParams.get('sla') === '1';

  const effectiveSearch = appliedSearchTerm || filterNumber || filterDescription;

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const savedPrefsHydratedRef = useRef(false);

  // Get browser's timezone offset (in minutes from UTC)
  // This is used to ensure date filtering is consistent with user's local timezone
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  // Filter preferences
  const {
    filters: savedFilters,
    saveFilters,
    isSaving: isFilterSaving,
    isFetched: isFilterPrefsFetched,
  } = useFilterPreferences('CASE');

  // Apply saved preferences to URL once when URL is still default (no explicit filters)
  useEffect(() => {
    if (filtersInitialized) return;
    if (user?.id && !isFilterPrefsFetched) return;

    if (!isDefaultCaseListUrl(searchParams)) {
      setFiltersInitialized(true);
      return;
    }

    if (savedPrefsHydratedRef.current) {
      setFiltersInitialized(true);
      return;
    }
    savedPrefsHydratedRef.current = true;

    const updates = buildCaseListUrlUpdatesFromSaved(savedFilters);
    if (Object.keys(updates).length > 0) {
      patchSearchParams(updates);
    }
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    isFilterPrefsFetched,
    user?.id,
    searchParams,
    savedFilters,
    patchSearchParams,
  ]);

  // Check if current filters differ from saved
  const savedEngagement = savedFilters.engagement
    ? (typeof savedFilters.engagement === 'string' ? savedFilters.engagement.split(',') : savedFilters.engagement)
    : [];
  const savedAssignmentGroup = savedFilters.assignmentGroup
    ? (typeof savedFilters.assignmentGroup === 'string' ? savedFilters.assignmentGroup.split(',') : savedFilters.assignmentGroup)
    : [];
  // Convert saved assignedToMe to string for comparison ('' if not set, 'true' or 'false' if set)
  const savedAssignedToMe = savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null
    ? (savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false')
    : '';
  const hasUnsavedFilterChanges = filtersInitialized && (
    JSON.stringify(filterState.sort()) !== JSON.stringify((savedFilters.state || []).sort()) ||
    JSON.stringify(filterPriority.sort()) !== JSON.stringify((savedFilters.priority || []).sort()) ||
    filterTimeRange !== (savedFilters.timeRange || '') ||
    filterCustomer !== (savedFilters.customer || '') ||
    JSON.stringify([...filterEngagement].sort()) !== JSON.stringify([...savedEngagement].sort()) ||
    filterCategory !== (savedFilters.category || '') ||
    filterChannel !== (savedFilters.channel || '') ||
    JSON.stringify([...filterAssignmentGroup].sort()) !== JSON.stringify([...savedAssignmentGroup].sort()) ||
    filterAssignedToMe !== savedAssignedToMe ||
    filterAssignedTo !== (savedFilters.assignedTo || '') ||
    filterUnassigned !== (savedFilters.unassigned || '')
  );

  const handleSaveFilters = async () => {
    await saveFilters({
      state: filterState,
      priority: filterPriority,
      timeRange: filterTimeRange || undefined,
      customer: filterCustomer || undefined,
      engagement: filterEngagement.length > 0 ? filterEngagement.join(',') : undefined,
      category: filterCategory || undefined,
      channel: filterChannel || undefined,
      assignmentGroup: filterAssignmentGroup.length > 0 ? filterAssignmentGroup.join(',') : undefined,
      assignedToMe: filterAssignedToMe || undefined,
      assignedTo: filterAssignedTo || undefined,
      unassigned: filterUnassigned || undefined,
    });
  };

  // Build export filters for background export
  const exportFilters = useMemo(() => ({
    ...(effectiveSearch && { search: effectiveSearch }),
    ...(filterState.length > 0 && { state: filterState.join(',') }),
    ...(filterCategory && { category: filterCategory }),
    ...(filterChannel && { channel: filterChannel }),
    ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
    ...(filterTimeRange && { timeRange: filterTimeRange }),
    ...(filterCustomer && { customerId: filterCustomer }),
    ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
    ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
    ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
    ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
    ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
    ...(filterUnassigned && { unassigned: filterUnassigned }),
    ...(filterImpact.length > 0 && { impact: filterImpact.join(',') }),
    ...(filterUrgency.length > 0 && { urgency: filterUrgency.join(',') }),
    ...(filterContact && { callerId: filterContact }),
    ...(filterCreatedBy && { createdById: filterCreatedBy }),
    ...(filterSlaBreached && { slaBreached: 'true' }),
  }), [effectiveSearch, filterState, filterCategory, filterChannel, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterContact, filterCreatedBy, filterSlaBreached]);

  // Email modal state
  const [emailModalState, setEmailModalState] = useState({
    isOpen: false,
    ticketId: '',
    ticketNumber: '',
    shortDescription: ''
  });

  const openEmailModal = (caseItem: Case) => {
    setEmailModalState({
      isOpen: true,
      ticketId: caseItem.id || caseItem._id || '',
      ticketNumber: caseItem.number,
      shortDescription: caseItem.shortDescription
    });
  };

  // Bulk selection state
  const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
  const [bulkActionType, setBulkActionType] = useState<'assign' | 'changeState' | 'addNotes' | null>(null);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // Bulk update errors modal state
  const [bulkUpdateErrors, setBulkUpdateErrors] = useState<Array<{
    caseId: string;
    caseNumber: string;
    success: boolean;
    error: string;
  }> | null>(null);

  // List layout settings
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const {
    columns: layoutColumns,
    visibleColumns,
    saveColumns,
    resetToDefault,
    isSaving: isLayoutSaving,
    isResetting: isLayoutResetting,
  } = useListLayoutPreferences('CASE');

  // SLA-related columns to hide for contact users
  const SLA_COLUMNS = ['slaPlan', 'slaBreached', 'responseBreachReason', 'ongoingBreachReason', 'resolutionBreachReason'];

  // Filter out SLA columns for contact users
  const filteredVisibleColumns = useMemo(() => {
    if (isContact()) {
      return visibleColumns.filter(col => !SLA_COLUMNS.includes(col.key));
    }
    return visibleColumns;
  }, [visibleColumns, isContact]);

  // Build columns array from visible columns for export. Replace single "SLA Breached"
  // with separate Response/Ongoing/Resolution SLA Status columns in CSV.
  const exportColumns = useMemo(() => {
    const cols: { key: string; header: string }[] = [];
    for (const col of filteredVisibleColumns) {
      if (col.key === 'slaBreached') {
        cols.push({ key: 'responseSlaStatus', header: 'Response SLA Status' });
        cols.push({ key: 'ongoingSlaStatus', header: 'Ongoing SLA Status' });
        cols.push({ key: 'resolutionSlaStatus', header: 'Resolution SLA Status' });
      } else if (col.key === 'customer') {
        cols.push({ key: col.key, header: col.label || col.key });
        cols.push({ key: 'customerType', header: 'Customer Type' });
      } else {
        cols.push({ key: col.key, header: col.label || col.key });
      }
    }
    return cols;
  }, [filteredVisibleColumns]);

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers/dropdown');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch engagements for selected customer (use UI state for immediate dropdown update)
  // For contact users, use a special endpoint that returns their customer's engagements
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', isContactUser ? 'my-customer' : filterCustomer],
    queryFn: async () => {
      if (isContactUser) {
        // Contact users use a special endpoint that doesn't require engagements:read permission
        const response = await api.get('/engagements/my-customer-engagements');
        return response.data;
      }
      if (!filterCustomer) return { engagements: [] };
      const response = await api.get(`/engagements/dropdown?customerId=${filterCustomer}`);
      return response.data;
    },
    enabled: isContactUser || !!filterCustomer
  });

  // Fetch assignment groups for filter dropdowns
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const mapDropdownUserToOption = (u: { id: string; name?: string; email?: string }) => ({
    value: u.id,
    label: u.name || u.email || u.id,
  });

  // Fetch users for filter dropdowns
  const { data: usersData } = useQuery({
    queryKey: ['users-for-filter'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: contactsDropdownData } = useQuery({
    queryKey: ['contacts-dropdown', filterCustomer],
    queryFn: async () => {
      const contactParams = new URLSearchParams();
      if (filterCustomer) contactParams.set('customerId', filterCustomer);
      const contactsRes = await api.get(
        `/contacts/dropdown${contactParams.toString() ? `?${contactParams}` : ''}`
      );
      return contactsRes.data?.contacts ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const contactFilterOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();

    for (const contact of contactsDropdownData ?? []) {
      if (!contact.id || seen.has(contact.id)) continue;
      seen.add(contact.id);
      options.push({
        value: contact.id,
        label: contact.name || contact.email || contact.id,
      });
    }

    for (const u of usersData?.users ?? []) {
      if (!u.id || seen.has(u.id)) continue;
      seen.add(u.id);
      options.push(mapDropdownUserToOption(u));
    }

    options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return options;
  }, [contactsDropdownData, usersData]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['cases', page, pageSize, effectiveSearch, filterState, filterCategory, filterChannel, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterContact, filterCreatedBy, filterSlaBreached, timezoneOffset],
    queryFn: async () => {
      // Build time range params
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ticketType: 'CASE',
        ...(effectiveSearch && { search: effectiveSearch }),
        ...(filterState.length > 0 && { state: filterState.join(',') }),
        ...(filterCategory && { category: filterCategory }),
        ...(filterChannel && { channel: filterChannel }),
        ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
        ...(timeRangeParam && { timeRange: timeRangeParam }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        // Include timezone offset for proper date filtering when date filters are present
        ...((dateFrom || dateTo) && { timezoneOffset: String(timezoneOffset) }),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
        ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
        ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
        ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
        ...(filterUnassigned && { unassigned: filterUnassigned }),
        ...(filterImpact.length > 0 && { impact: filterImpact.join(',') }),
        ...(filterUrgency.length > 0 && { urgency: filterUrgency.join(',') }),
        ...(filterContact && { callerId: filterContact }),
        ...(filterCreatedBy && { createdById: filterCreatedBy }),
        ...(filterSlaBreached && { slaBreached: 'true' }),
      });

      const response = await api.get(`/cases?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000 // 30 seconds
  });

  // Log Cases List Viewed (only once per page load)
  useEffect(() => {
    if (data && user && !hasLoggedView.current) {
      hasLoggedView.current = true;
      trackCaseOperation(
        'Cases List',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Cases List Viewed',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [data, user, isServiceDeskUser]);

  // Fetch case stats for the summary bar (with same filters as list)
  const { data: statsData } = useQuery({
    queryKey: ['case-stats', effectiveSearch, filterState, filterCategory, filterChannel, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterContact, filterCreatedBy, filterSlaBreached, timezoneOffset],
    queryFn: async () => {
      // Build time range params (same as list query)
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams({
        ticketType: 'CASE',
        ...(effectiveSearch && { search: effectiveSearch }),
        ...(filterState.length > 0 && { state: filterState.join(',') }),
        ...(filterCategory && { category: filterCategory }),
        ...(filterChannel && { channel: filterChannel }),
        ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
        ...(timeRangeParam && { timeRange: timeRangeParam }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        // Include timezone offset for proper date filtering when date filters are present
        ...((dateFrom || dateTo) && { timezoneOffset: String(timezoneOffset) }),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
        ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
        ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
        ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
        ...(filterUnassigned && { unassigned: filterUnassigned }),
        ...(filterImpact.length > 0 && { impact: filterImpact.join(',') }),
        ...(filterUrgency.length > 0 && { urgency: filterUrgency.join(',') }),
        ...(filterContact && { callerId: filterContact }),
        ...(filterCreatedBy && { createdById: filterCreatedBy }),
        ...(filterSlaBreached && { slaBreached: 'true' }),
      });

      const response = await api.get(`/cases/stats?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds - match list query
    enabled: !isLoading,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchInput.trim() || null,
      page: '1',
    });
  };

  // Build filter sections for FilterDropdown
  const filterSections: FilterSection[] = useMemo(() => {
    const sections: FilterSection[] = [
      {
        id: 'state',
        label: 'State',
        type: 'multi-select',
        options: stateOptions,
        placeholder: 'All States',
      },
      {
        id: 'priority',
        label: 'Priority',
        type: 'multi-select',
        options: priorityOptions,
        placeholder: 'All Priorities',
      },
      {
        id: 'timeRange',
        label: 'Time Range',
        type: 'time-range',
        options: timeRangeOptions,
        placeholder: 'All Time',
      },
    ];

    // Only add Customer filter for non-contact users
    if (!isContactUser) {
      sections.push({
        id: 'customer',
        label: 'Customer',
        type: 'searchable-select',
        options: customersData?.customers?.map((c: any) => ({
          value: c.id,
          label: c.accountName || c.name,
        })) || [],
        placeholder: 'All Customers',
      });
    }

    // Engagement filter - for contact users, show their customer's engagements directly
    // For non-contact users, require customer selection first
    sections.push({
      id: 'engagement',
      label: 'Engagement',
      type: 'searchable-multi-select',
      options: engagementsData?.engagements?.map((e: any) => ({
        value: e.id,
        label: e.name,
      })) || [],
      placeholder: 'All Engagements',
      disabled: isContactUser ? false : !filterCustomer,
      disabledMessage: isContactUser ? undefined : 'Select a customer first',
    });

    // Category filter only for non-contact users
    if (!isContactUser) {
      sections.push({
        id: 'category',
        label: 'Category',
        type: 'single-select',
        options: [
          { value: 'question', label: 'Question' },
          { value: 'issue', label: 'Issue' },
          { value: 'request', label: 'Request' },
          { value: 'change', label: 'Change' },
        ],
        placeholder: 'All Categories',
      });
    }

    sections.push({
      id: 'channel',
      label: 'Channel',
      type: 'single-select',
      options: [
        { value: 'web', label: 'Web' },
        { value: 'phone', label: 'Phone' },
        { value: 'email', label: 'Email' },
        { value: 'alert', label: 'Alert' },
      ],
      placeholder: 'All Channels',
    });

    // Only add these filters for non-contact users
    if (!isContactUser) {
      sections.push(
        {
          id: 'alertNumber',
          label: 'Alert Number',
          type: 'text',
          placeholder: 'e.g., ALT001, ALT002',
          helpText: 'Enter single or multiple alert numbers separated by commas',
        },
        {
          id: 'assignmentGroup',
          label: 'Assignment Group',
          type: 'searchable-multi-select',
          options: assignmentGroupsData?.map((g: { id: string; name: string }) => ({
            value: g.id,
            label: g.name,
          })) || [],
          placeholder: 'All Groups',
        },
        {
          id: 'assignedToMe',
          label: 'Assigned To Me',
          type: 'single-select',
          options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ],
          placeholder: 'All Assignments',
        },
        {
          id: 'assignedTo',
          label: 'Assigned To',
          type: 'searchable-select',
          options: usersData?.users?.map(mapDropdownUserToOption) || [],
          placeholder: 'All Users',
        },
        {
          id: 'unassigned',
          label: 'Show Unassigned',
          type: 'single-select',
          options: [
            { value: 'true', label: 'Yes' },
          ],
          placeholder: 'All',
        }
      );
    }

    return sections;
  }, [customersData, engagementsData, assignmentGroupsData, usersData, filterCustomer, isContactUser]);

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountShortName?: string; accountName?: string; name?: string }) => ({
        value: c.id,
        label: c.accountShortName || c.accountName || c.name || c.id,
      })) ?? [],
    [customersData]
  );

  const engagementOptions = useMemo(
    () =>
      engagementsData?.engagements?.map((e: { id: string; name: string }) => ({
        value: e.id,
        label: e.name,
      })) ?? [],
    [engagementsData]
  );

  const assignmentGroupOptions = useMemo(
    () =>
      assignmentGroupsData?.map((g: { id: string; name: string }) => ({
        value: g.id,
        label: g.name,
      })) ?? [],
    [assignmentGroupsData]
  );

  const userFilterOptions = useMemo(
    (): Array<{ value: string; label: string }> =>
      usersData?.users?.map(mapDropdownUserToOption) ?? [],
    [usersData]
  );

  const assignedToOptions = useMemo(
    () => [{ value: '__unassigned__', label: 'Unassigned' }, ...userFilterOptions],
    [userFilterOptions]
  );

  const handleAssignedToColumnFilter = useCallback(
    (value: string | string[]) => {
      const v = typeof value === 'string' ? value : '';
      if (v === '__unassigned__') {
        patchSearchParams({ assignedTo: null, unassigned: 'true', page: '1' });
      } else if (v) {
        patchSearchParams({ assignedTo: v, unassigned: null, page: '1' });
      } else {
        patchSearchParams({ assignedTo: null, unassigned: null, page: '1' });
      }
    },
    [patchSearchParams]
  );

  const assignedToColumnValue = filterUnassigned === 'true' ? '__unassigned__' : filterAssignedTo;

  const createdTimeRangeOptions = useMemo(
    () => [...timeRangeOptions, { value: 'all', label: 'All Time' }],
    []
  );

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number (comma or space separated)..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'shortDescription':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDescription}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'contact':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={contactFilterOptions ?? []}
              value={filterContact}
              placeholder="All Contacts"
              onChange={(v) => {
                patchSearchParams({ contact: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'customer':
          if (isContactUser) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({
                  customer: typeof v === 'string' && v ? v : null,
                  engagement: null,
                  page: '1',
                });
              }}
            />
          );
        case 'engagement':
          if (!isContactUser && !filterCustomer) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'channel':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={channelOptions}
              value={filterChannel}
              placeholder="All Channels"
              onChange={(v) => {
                patchSearchParams({ channel: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'state':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={stateOptions}
              value={filterState}
              placeholder="All States"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ state: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'priority':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={priorityOptions}
              value={filterPriority}
              placeholder="All Priorities"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ priority: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'assignedTo':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={assignedToOptions}
              value={assignedToColumnValue}
              placeholder="All Users"
              onChange={handleAssignedToColumnFilter}
            />
          );
        case 'assignmentGroup':
          if (isContactUser) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={assignmentGroupOptions}
              value={filterAssignmentGroup}
              placeholder="All Groups"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ groups: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'category':
          if (isContactUser) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={categoryOptions}
              value={filterCategory}
              placeholder="All Categories"
              onChange={(v) => {
                patchSearchParams({ category: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'impact':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={impactOptions}
              value={filterImpact}
              placeholder="All Impact"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ impact: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'urgency':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={urgencyOptions}
              value={filterUrgency}
              placeholder="All Urgency"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ urgency: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'createdBy':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={userFilterOptions}
              value={filterCreatedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ createdBy: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'createdAt':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={createdTimeRangeOptions}
              value={filterTimeRange || 'all'}
              placeholder="All Time"
              active={searchParams.has('time')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                const timeParam = serializeCaseTimeRangeForUrl(val === 'all' ? '' : val);
                patchSearchParams({ time: timeParam, page: '1' });
              }}
            />
          );
        case 'slaBreached':
          if (isContactUser) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={[{ value: 'true', label: 'SLA Breached' }]}
              value={filterSlaBreached ? 'true' : ''}
              placeholder="All"
              onChange={(v) => {
                patchSearchParams({ sla: v === 'true' ? '1' : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterDescription,
      contactFilterOptions,
      filterContact,
      isContactUser,
      customerOptions,
      filterCustomer,
      engagementOptions,
      filterEngagement,
      filterChannel,
      filterState,
      filterPriority,
      assignedToOptions,
      assignedToColumnValue,
      handleAssignedToColumnFilter,
      assignmentGroupOptions,
      filterAssignmentGroup,
      filterCategory,
      filterImpact,
      filterUrgency,
      userFilterOptions,
      filterCreatedBy,
      createdTimeRangeOptions,
      filterTimeRange,
      searchParams,
      filterSlaBreached,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({ id: 'number', label: 'Number', value: filterNumber, color: 'blue' });
    }

    if (filterDescription) {
      chips.push({ id: 'desc', label: 'Description', value: filterDescription, color: 'blue' });
    }

    if (filterContact) {
      const option = contactFilterOptions?.find((o) => o.value === filterContact);
      chips.push({
        id: 'contact',
        label: 'Contact',
        value: option?.label || filterContact,
        color: 'teal',
      });
    }

    if (filterState.length > 0) {
      chips.push({
        id: 'state',
        label: 'State',
        value: filterState.map(s => stateLabels[s as keyof typeof stateLabels] || s).join(', '),
        color: 'purple',
      });
    }

    if (filterPriority.length > 0) {
      chips.push({
        id: 'priority',
        label: 'Priority',
        value: filterPriority.map(p => priorityLabels[p as keyof typeof priorityLabels] || p).join(', '),
        color: 'orange',
      });
    }

    if (filterTimeRange) {
      let timeLabel = '';
      if (filterTimeRange.startsWith('custom:')) {
        const [, dates] = filterTimeRange.split('custom:');
        const [from, to] = dates.split(',');
        timeLabel = `${from} to ${to}`;
      } else {
        const timeOption = timeRangeOptions.find(t => t.value === filterTimeRange);
        timeLabel = timeOption?.label || filterTimeRange;
      }
      chips.push({
        id: 'timeRange',
        label: 'Time',
        value: timeLabel,
        color: 'orange',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: any) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountName || customer?.name || 'Unknown',
        color: 'teal',
      });
    }

    if (filterEngagement.length > 0) {
      const engagementNames = filterEngagement.map(engId => {
        const engagement = engagementsData?.engagements?.find((e: any) => e.id === engId);
        return engagement?.name || 'Unknown';
      });
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: engagementNames.join(', '),
        color: 'cyan',
      });
    }

    if (filterCategory) {
      chips.push({
        id: 'category',
        label: 'Category',
        value: filterCategory.charAt(0).toUpperCase() + filterCategory.slice(1),
        color: 'purple',
      });
    }

    if (filterChannel) {
      chips.push({
        id: 'channel',
        label: 'Channel',
        value: filterChannel.charAt(0).toUpperCase() + filterChannel.slice(1),
        color: 'orange',
      });
    }

    if (appliedAlertNumber) {
      chips.push({
        id: 'alertNumber',
        label: 'Alert Number',
        value: appliedAlertNumber,
        color: 'red',
      });
    }

    if (filterAssignmentGroup.length > 0) {
      const groupNames = filterAssignmentGroup.map(groupId => {
        const group = assignmentGroupsData?.find((g: { id: string; name: string }) => g.id === groupId);
        return group?.name || 'Unknown';
      });
      chips.push({
        id: 'assignmentGroup',
        label: 'Assignment Group',
        value: groupNames.join(', '),
        color: 'purple',
      });
    }

    if (filterAssignedToMe) {
      chips.push({
        id: 'assignedToMe',
        label: 'Assigned To Me',
        value: filterAssignedToMe === 'true' ? 'Yes' : 'No',
        color: filterAssignedToMe === 'true' ? 'green' : 'gray',
      });
    }

    if (filterAssignedTo) {
      const assignedUser = userFilterOptions.find((o) => o.value === filterAssignedTo);
      chips.push({
        id: 'assignedTo',
        label: 'Assigned To',
        value: assignedUser?.label || 'Unknown',
        color: 'teal',
      });
    }

    if (filterImpact.length > 0) {
      chips.push({
        id: 'impact',
        label: 'Impact',
        value: filterImpact.map((v) => impactOptions.find((o) => o.value === v)?.label || v).join(', '),
        color: 'orange',
      });
    }

    if (filterUrgency.length > 0) {
      chips.push({
        id: 'urgency',
        label: 'Urgency',
        value: filterUrgency.map((v) => urgencyOptions.find((o) => o.value === v)?.label || v).join(', '),
        color: 'orange',
      });
    }

    if (filterCreatedBy) {
      const user = userFilterOptions.find((o) => o.value === filterCreatedBy);
      chips.push({
        id: 'createdBy',
        label: 'Created By',
        value: user?.label || filterCreatedBy,
        color: 'teal',
      });
    }

    if (filterSlaBreached) {
      chips.push({
        id: 'sla',
        label: 'SLA Breached',
        value: 'Yes',
        color: 'red',
      });
    }

    if (filterUnassigned === 'true') {
      chips.push({
        id: 'unassigned',
        label: 'Show Unassigned',
        value: 'Yes',
        color: 'orange',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDescription, filterContact, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, filterCategory, filterChannel, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterImpact, filterUrgency, filterCreatedBy, filterSlaBreached, customersData, engagementsData, assignmentGroupsData, contactFilterOptions, userFilterOptions]);

  // Handle filter dropdown changes
  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'state': {
        const arr = value as string[];
        patchSearchParams({
          state: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'priority': {
        const arr = value as string[];
        patchSearchParams({
          priority: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'timeRange': {
        const v = value as string;
        const timeParam = serializeCaseTimeRangeForUrl(v);
        patchSearchParams({
          time: timeParam,
          page: '1',
        });
        break;
      }
      case 'customer':
        patchSearchParams({
          customer: (value as string) || null,
          engagement: null,
          page: '1',
        });
        break;
      case 'engagement': {
        const arr = value as string[];
        patchSearchParams({
          engagement: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'category':
        patchSearchParams({
          category: (value as string) || null,
          page: '1',
        });
        break;
      case 'channel':
        patchSearchParams({
          channel: (value as string) || null,
          page: '1',
        });
        break;
      case 'alertNumber':
        setFilterAlertNumber(value as string);
        break;
      case 'assignmentGroup': {
        const arr = value as string[];
        patchSearchParams({
          groups: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'assignedToMe':
        patchSearchParams({
          assignedToMe:
            value === 'true' || value === 'false' ? (value as string) : null,
          page: '1',
        });
        break;
      case 'assignedTo':
        patchSearchParams({
          assignedTo: (value as string) || null,
          page: '1',
        });
        break;
      case 'unassigned':
        patchSearchParams({
          unassigned: value === 'true' ? 'true' : null,
          page: '1',
        });
        break;
    }
  };

  // Handle applying alert number filter
  const handleApplyFilters = () => {
    if (filterAlertNumber !== appliedAlertNumber) {
      patchSearchParams({
        alert: filterAlertNumber.trim() || null,
        page: '1',
      });
    }
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'contact':
        patchSearchParams({ contact: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: null, page: '1' });
        break;
      case 'priority':
        patchSearchParams({ priority: null, page: '1' });
        break;
      case 'timeRange':
        patchSearchParams({ time: 'all', page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, engagement: null, page: '1' });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null, page: '1' });
        break;
      case 'category':
        patchSearchParams({ category: null, page: '1' });
        break;
      case 'channel':
        patchSearchParams({ channel: null, page: '1' });
        break;
      case 'alertNumber':
        setFilterAlertNumber('');
        patchSearchParams({ alert: null, page: '1' });
        break;
      case 'assignmentGroup':
        patchSearchParams({ groups: null, page: '1' });
        break;
      case 'assignedToMe':
        patchSearchParams({ assignedToMe: null, page: '1' });
        break;
      case 'assignedTo':
        patchSearchParams({ assignedTo: null, page: '1' });
        break;
      case 'unassigned':
        patchSearchParams({ unassigned: null, page: '1' });
        break;
      case 'impact':
        patchSearchParams({ impact: null, page: '1' });
        break;
      case 'urgency':
        patchSearchParams({ urgency: null, page: '1' });
        break;
      case 'createdBy':
        patchSearchParams({ createdBy: null, page: '1' });
        break;
      case 'sla':
        patchSearchParams({ sla: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSearchInput('');
    setFilterAlertNumber('');
    patchSearchParams({
      search: null,
      number: null,
      desc: null,
      contact: null,
      state: null,
      priority: null,
      time: 'all',
      customer: null,
      engagement: null,
      alert: null,
      groups: null,
      assignedToMe: null,
      assignedTo: null,
      unassigned: null,
      category: null,
      channel: null,
      impact: null,
      urgency: null,
      createdBy: null,
      sla: null,
      page: '1',
    });
  };

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select cases that are not closed, cancelled, or resolved
      const selectableIds = data?.cases
        ?.filter((c: Case) => c.state !== 'closed' && c.state !== 'cancelled' && c.state !== 'resolved')
        .map((c: Case) => c.id || c._id) || [];
      setSelectedCases(new Set(selectableIds));
    } else {
      setSelectedCases(new Set());
    }
  };

  const handleSelectCase = (caseId: string, checked: boolean) => {
    const newSelected = new Set(selectedCases);
    if (checked) {
      newSelected.add(caseId);
    } else {
      newSelected.delete(caseId);
    }
    setSelectedCases(newSelected);
  };

  // Calculate selectable cases (not closed, cancelled, or resolved)
  const selectableCases = data?.cases?.filter((c: Case) => c.state !== 'closed' && c.state !== 'cancelled' && c.state !== 'resolved') || [];
  const isAllSelected = selectableCases.length > 0 && selectedCases.size === selectableCases.length;
  const isSomeSelected = selectedCases.size > 0 && selectedCases.size < selectableCases.length;

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async (updates: any) => {
      const response = await api.put('/cases/bulk-update', {
        caseIds: Array.from(selectedCases),
        updates
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      setSelectedCases(new Set());
      setBulkActionType(null);

      // Check if there were any validation errors during bulk update
      if (data.errors && data.errors.length > 0) {
        // Show warning toast and open error details modal
        setToast({
          message: `${data.results?.length || 0} cases updated, ${data.errors.length} failed. Click to see details.`,
          type: data.results?.length > 0 ? 'warning' : 'error'
        });
        setBulkUpdateErrors(data.errors);
        setTimeout(() => setToast(null), 10000); // Keep toast longer for errors
      } else {
        setToast({
          message: data.message || 'Cases updated successfully',
          type: 'success'
        });
        setTimeout(() => setToast(null), 5000);
      }
    },
    onError: (error: any) => {
      setToast({
        message: `Error updating cases: ${error.response?.data?.message || error.message}`,
        type: 'error'
      });
      setTimeout(() => setToast(null), 5000);
    }
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading cases. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <form onSubmit={handleSearch} className="flex items-center justify-between gap-4">
            {/* Left side: Title and Filter dropdown */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-bold text-gray-900 whitespace-nowrap">Cases</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  state: filterState,
                  priority: filterPriority,
                  timeRange: filterTimeRange,
                  customer: filterCustomer,
                  engagement: filterEngagement,
                  category: filterCategory,
                  channel: filterChannel,
                  alertNumber: filterAlertNumber,
                  assignmentGroup: filterAssignmentGroup,
                  assignedToMe: filterAssignedToMe,
                  assignedTo: filterAssignedTo,
                  unassigned: filterUnassigned,
                }}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
              />
            </div>

            {/* Right side: Search, New button, Settings */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search cases or numbers (comma/space separated)..."
                  className="pl-9 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={!isTicketSearchInputValid(searchInput)}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Search
              </button>
              <Link
                to="/cases/new"
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1 h-4 w-4" />
                New
              </Link>
              {!isContactUser && (
                <ListLayoutSettingsButton onClick={() => setShowLayoutSettings(true)} />
              )}
              <RefreshButton
                queryKeys={['cases']}
                tooltip="Refresh cases"
                size="md"
                isLoading={isLoading}
              />
            </div>
          </form>
        </div>
      </div>

      {/* Stats Summary Bar */}
      <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <span className="text-gray-600">Total:</span>
              <span className="font-semibold text-gray-900">{statsData?.total ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-gray-600">Critical:</span>
              <span className="font-semibold text-gray-900">{statsData?.byPriority?.critical_1 ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              <span className="text-gray-600">High:</span>
              <span className="font-semibold text-gray-900">{statsData?.byPriority?.high_2 ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-gray-600">In Progress:</span>
              <span className="font-semibold text-gray-900">{statsData?.byState?.in_progress ?? '-'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              <span className="text-gray-600">Unassigned:</span>
              <span className="font-semibold text-gray-900">{statsData?.unassigned ?? '-'}</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Export CSV Button - hidden for contact users */}
            {!isContact() && data?.cases && data.cases.length > 0 && (
              <GenericExportButton
                config={ExportConfigs.CASES}
                filters={exportFilters}
                columns={exportColumns}
                totalRows={data?.total}
                disabled={!data?.cases || data.cases.length === 0}
                onExportStart={() => {
                  if (user) {
                    trackCaseOperation(
                      'Cases List',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Cases List Export Clicked',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
              />
            )}
            {/* SLA Breached warning - hidden for contact users */}
            {!isContact() && (statsData?.slaBreached ?? 0) > 0 && (
              <span className="flex items-center gap-1.5 text-red-600">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>SLA Breached:</span>
                <span className="font-semibold">{statsData?.slaBreached}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {activeFilterChips.length > 0 && (
        <div className="mb-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
            onSaveAsDefault={handleSaveFilters}
            isSaving={isFilterSaving}
            hasUnsavedChanges={hasUnsavedFilterChanges}
          />
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedCases.size > 0 && hasUpdatePermission && !isContactUser && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 mb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-100 text-indigo-800">
                {selectedCases.size} selected
              </span>
            </div>
            <div className="flex gap-3">
              {/* Actions Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                </button>

                {showActionsDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowActionsDropdown(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setBulkActionType('assign');
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Assign</span>
                        </button>
                        <button
                          onClick={() => {
                            setBulkActionType('changeState');
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Change State</span>
                        </button>
                        <button
                          onClick={() => {
                            setBulkActionType('addNotes');
                            setShowActionsDropdown(false);
                          }}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Add Notes</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={() => setSelectedCases(new Set())}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cases Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {hasUpdatePermission && !isContactUser && (
                <th className="px-3 py-2 text-left bg-gray-50">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate = isSomeSelected;
                      }
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={selectableCases.length === 0}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    title={selectableCases.length === 0 ? 'No selectable cases' : 'Select all selectable cases'}
                  />
                </th>
              )}
              {filteredVisibleColumns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader(col.key, col.label)}
                </th>
              ))}
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.cases?.map((caseItem: Case) => {
              const caseId = caseItem.id || caseItem._id || '';
              const isDisabled = caseItem.state === 'closed' || caseItem.state === 'cancelled' || caseItem.state === 'resolved';

              // Column renderer function
              const renderColumn = (columnKey: string) => {
                switch (columnKey) {
                  case 'number':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Link
                            to={isContactUser ? `/tickets/${caseItem.id || caseItem._id}?from=cases` : `/cases/${caseItem.id || caseItem._id}`}
                            state={{ casesListSearch: location.search }}
                            className="text-indigo-600 hover:text-indigo-900 hover:underline"
                            title="View case details"
                          >
                            {caseItem.number}
                          </Link>
                          {caseItem.relatedRecordsCount !== undefined && caseItem.relatedRecordsCount > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                              title={`${caseItem.relatedRecordsCount} related record${caseItem.relatedRecordsCount > 1 ? 's' : ''}`}
                            >
                              {caseItem.relatedRecordsCount}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  case 'shortDescription':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{stripHtml(caseItem.shortDescription)}</div>
                      </td>
                    );
                  case 'contact':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.contact ? (
                          `${caseItem.contact.firstName || ''} ${caseItem.contact.lastName || ''}`.trim() ||
                          caseItem.contact.email ||
                          '-'
                        ) : '-'}
                      </td>
                    );
                  case 'customer':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.Customer ? caseItem.Customer.accountName : '-'}
                      </td>
                    );
                  case 'channel':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          {channelIcons[caseItem.channel as keyof typeof channelIcons] || channelIcons.web}
                          <span className="ml-2 capitalize">{caseItem.channel?.replace('_', ' ') || 'Web'}</span>
                        </div>
                      </td>
                    );
                  case 'state':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateColors[caseItem.state as keyof typeof stateColors] || 'bg-gray-100 text-gray-800'}`}>
                          {stateLabels[caseItem.state as keyof typeof stateLabels] || caseItem.state}
                        </span>
                      </td>
                    );
                  case 'priority':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[caseItem.priority as keyof typeof priorityColors] || 'bg-gray-100 text-gray-800'}`}>
                          {priorityLabels[caseItem.priority as keyof typeof priorityLabels] || caseItem.priority}
                        </span>
                      </td>
                    );
                  case 'assignedTo':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        {caseItem.assignedTo ? (
                          <span className="font-medium">
                            {`${caseItem.assignedTo.firstName || ''} ${caseItem.assignedTo.lastName || ''}`.trim() ||
                              caseItem.assignedTo.email ||
                              <span className="text-gray-500 font-normal">Unassigned</span>}
                          </span>
                        ) : (
                          <span className="text-gray-500">Unassigned</span>
                        )}
                      </td>
                    );
                  case 'assignmentGroup':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.AssignmentGroup ? caseItem.AssignmentGroup.name : '-'}
                      </td>
                    );
                  case 'impact':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.impact ? caseItem.impact.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'urgency':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.urgency ? caseItem.urgency.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'category':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.category || '-'}
                      </td>
                    );
                  case 'slaPlan':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.ServicePlan ? caseItem.ServicePlan.name : '-'}
                      </td>
                    );
                  case 'engagement':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.Engagement ? caseItem.Engagement.name : '-'}
                      </td>
                    );
                  case 'resource':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.Resource ? caseItem.Resource.name : '-'}
                      </td>
                    );
                  case 'slaBreached':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <SlaStatusDisplay slaStatus={caseItem.slaStatus} />
                      </td>
                    );
                  case 'responseBreachReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <TruncatedTextWithTooltip text={(caseItem as any).responseBreachReason} />
                      </td>
                    );
                  case 'ongoingBreachReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <TruncatedTextWithTooltip text={(caseItem as any).ongoingBreachReason} />
                      </td>
                    );
                  case 'resolutionBreachReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <TruncatedTextWithTooltip text={(caseItem as any).resolutionBreachReason} />
                      </td>
                    );
                  case 'onHoldReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(caseItem as any).onHoldReason ? stripHtml((caseItem as any).onHoldReason) : '-'}</div>
                      </td>
                    );
                  case 'solution':
                  case 'solutionProposed':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={(caseItem as any).solution ? stripHtml((caseItem as any).solution) : ''}>
                          {(caseItem as any).solution ? stripHtml((caseItem as any).solution) : '-'}
                        </div>
                      </td>
                    );
                  case 'resolution':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={(caseItem as any).resolution ? stripHtml((caseItem as any).resolution) : ''}>
                          {(caseItem as any).resolution ? stripHtml((caseItem as any).resolution) : '-'}
                        </div>
                      </td>
                    );
                  case 'resolutionNotes':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={(caseItem as any).resolutionNotes ? stripHtml((caseItem as any).resolutionNotes) : ''}>
                          {(caseItem as any).resolutionNotes ? stripHtml((caseItem as any).resolutionNotes) : '-'}
                        </div>
                      </td>
                    );
                  case 'closureNotes':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate" title={(caseItem as any).closureNotes ? stripHtml((caseItem as any).closureNotes) : ''}>
                          {(caseItem as any).closureNotes ? stripHtml((caseItem as any).closureNotes) : '-'}
                        </div>
                      </td>
                    );
                  case 'closedAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(caseItem as any).closedAt ? new Date((caseItem as any).closedAt).toLocaleDateString() : '-'}
                      </td>
                    );
                  case 'updatedAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {caseItem.updatedAt ? new Date(caseItem.updatedAt).toLocaleString() : '-'}
                      </td>
                    );
                  case 'createdBy':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {caseItem.createdBy ?
                          `${caseItem.createdBy.firstName} ${caseItem.createdBy.lastName}` :
                          '-'
                        }
                      </td>
                    );
                  case 'createdAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {caseItem.createdAt ? (
                          <div className="flex flex-col">
                            <span>{new Date(caseItem.createdAt).toLocaleDateString()}</span>
                            <span className="text-xs">{new Date(caseItem.createdAt).toLocaleTimeString()}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  default:
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                    );
                }
              };

              return (
              <tr key={caseId} className="hover:bg-gray-50">
                {hasUpdatePermission && !isContactUser && (
                  <td className="px-3 py-2 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedCases.has(caseId)}
                      onChange={(e) => handleSelectCase(caseId, e.target.checked)}
                      disabled={isDisabled}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isDisabled ? 'Cannot select closed or cancelled cases' : ''}
                    />
                  </td>
                )}
                {filteredVisibleColumns.map((col) => renderColumn(col.key))}
                <td className="px-3 py-2 whitespace-nowrap text-sm">
                  <button
                    onClick={() => openEmailModal(caseItem)}
                    className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                    title="Email case details"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination Controls - Always visible */}
      {data?.cases && data.cases.length > 0 ? (
        <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = previousSize >= data.total && size > data.total;
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data.total)} to {Math.min(page * pageSize, data.total)} of {data.total} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil(data.total / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({
                  page: String(Math.ceil(data.total / pageSize)),
                })
              }
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-center bg-white px-4 py-8 rounded-lg shadow flex-shrink-0">
          <p className="text-gray-500">No cases found</p>
        </div>
      )}

      {/* Bulk Action Modals */}
      {bulkActionType === 'assign' && <AssignModal />}
      {bulkActionType === 'changeState' && <ChangeStateModal />}
      {bulkActionType === 'addNotes' && <AddNotesModal />}

      {/* Bulk Update Errors Modal */}
      {bulkUpdateErrors && bulkUpdateErrors.length > 0 && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setBulkUpdateErrors(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Update Errors</h3>
                <button
                  onClick={() => setBulkUpdateErrors(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-3 py-2 overflow-y-auto max-h-[60vh]">
                <p className="text-sm text-gray-600 mb-4">
                  The following cases could not be updated due to validation errors:
                </p>
                <div className="space-y-3">
                  {bulkUpdateErrors.map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <XMarkIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-800">
                            Case #{error.caseNumber}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {error.error}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-3 py-2 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setBulkUpdateErrors(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`rounded-lg shadow-lg px-3 py-2 max-w-md ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : toast.type === 'error'
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            } ${bulkUpdateErrors && bulkUpdateErrors.length > 0 ? 'cursor-pointer' : ''}`}
            onClick={() => {
              if (bulkUpdateErrors && bulkUpdateErrors.length > 0) {
                // Show the errors modal when clicking on warning/error toast
              }
            }}
          >
            <div className="flex items-start gap-3">
              {toast.type === 'success' && (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              )}
              {toast.type === 'error' && (
                <XMarkIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              {toast.type === 'warning' && (
                <QuestionMarkCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    toast.type === 'success'
                      ? 'text-green-800'
                      : toast.type === 'error'
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}
                >
                  {toast.message}
                </p>
                {bulkUpdateErrors && bulkUpdateErrors.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setToast(null);
                    }}
                    className={`text-xs mt-1 underline ${
                      toast.type === 'error'
                        ? 'text-red-600 hover:text-red-800'
                        : 'text-yellow-600 hover:text-yellow-800'
                    }`}
                  >
                    View error details
                  </button>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setToast(null);
                }}
                className={`flex-shrink-0 ${
                  toast.type === 'success'
                    ? 'text-green-400 hover:text-green-600'
                    : toast.type === 'error'
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-yellow-400 hover:text-yellow-600'
                }`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Ticket Modal */}
      <EmailTicketModal
        isOpen={emailModalState.isOpen}
        onClose={() => setEmailModalState({ isOpen: false, ticketId: '', ticketNumber: '', shortDescription: '' })}
        ticketId={emailModalState.ticketId}
        ticketType="CASE"
        ticketNumber={emailModalState.ticketNumber}
        shortDescription={emailModalState.shortDescription}
      />

      {/* List Layout Settings Modal */}
      <ListLayoutSettingsModal
        isOpen={showLayoutSettings}
        onClose={() => setShowLayoutSettings(false)}
        entityType="CASE"
        columns={layoutColumns}
        onSave={saveColumns}
        onReset={resetToDefault}
        isSaving={isLayoutSaving}
        isResetting={isLayoutResetting}
      />
    </div>
  );

  // Assign Modal Component
  function AssignModal() {
    const [formData, setFormData] = useState({
      assignedToId: '',
      assignedGroupId: ''
    });

    // Fetch assignment groups only (type = assignment)
    const { data: groupsData } = useQuery({
      queryKey: ['assignment-groups'],
      queryFn: async () => {
        const response = await api.get('/groups/assignment-groups');
        return response.data;
      }
    });

    // Fetch users from selected group
    const { data: usersData } = useQuery({
      queryKey: ['group-members', formData.assignedGroupId],
      queryFn: async () => {
        if (!formData.assignedGroupId) return [];
        const response = await api.get(`/groups/${formData.assignedGroupId}/members`);
        return response.data;
      },
      enabled: !!formData.assignedGroupId
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.assignedGroupId || !formData.assignedToId) {
        setToast({
          message: 'Please select both assignment group and assigned user',
          type: 'warning'
        });
        setTimeout(() => setToast(null), 5000);
        return;
      }

      const updates: any = {
        assignedGroupId: formData.assignedGroupId,
        assignedToId: formData.assignedToId
      };

      bulkUpdateMutation.mutate(updates);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="bg-white border-b border-gray-200 px-3 py-2 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Assign {selectedCases.size} Case{selectedCases.size > 1 ? 's' : ''}
              </h2>
              <button
                onClick={() => setBulkActionType(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Assignment Group */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignment Group <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.assignedGroupId}
                  onChange={(e) => {
                    setFormData({
                      assignedGroupId: e.target.value,
                      assignedToId: '' // Clear assigned user when group changes
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Assignment Group</option>
                  {groupsData?.map((group: any) => (
                    <option key={group.id} value={group.id}>
                      {group.name} {group.memberCount ? `(${group.memberCount} members)` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Assigned To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To
                </label>
                <select
                  value={formData.assignedToId}
                  onChange={(e) => setFormData({ ...formData, assignedToId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!formData.assignedGroupId}
                >
                  <option value="">
                    {!formData.assignedGroupId
                      ? 'Please select an assignment group first'
                      : usersData && usersData.length === 0
                      ? 'No users in this group'
                      : 'Select User'}
                  </option>
                  {usersData?.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
                {!formData.assignedGroupId && (
                  <p className="mt-1 text-sm text-gray-500">
                    Select an assignment group to see available users
                  </p>
                )}
                {formData.assignedGroupId && usersData && usersData.length === 0 && (
                  <p className="mt-1 text-sm text-amber-600">
                    This group has no members. Please select a different group or add members to this group first.
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setBulkActionType(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={bulkUpdateMutation.isPending}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
              >
                {bulkUpdateMutation.isPending ? 'Assigning...' : `Assign ${selectedCases.size} Case${selectedCases.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Change State Modal Component
  function ChangeStateModal() {
    const [formData, setFormData] = useState({
      state: '',
      notes: '',
      resolutionCode: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.state) {
        setToast({
          message: 'Please select a state',
          type: 'warning'
        });
        setTimeout(() => setToast(null), 5000);
        return;
      }

      const updates: any = { state: formData.state };

      // Add state-specific fields
      if (formData.state === 'awaiting_info' && formData.notes) {
        updates.infoRequired = formData.notes;
      } else if (formData.state === 'on_hold' && formData.notes) {
        updates.onHoldReason = formData.notes;
      } else if ((formData.state === 'closed' || formData.state === 'closed_complete' || formData.state === 'closed_incomplete')) {
        if (formData.notes) updates.resolutionNotes = formData.notes;
        if (formData.resolutionCode) updates.resolutionCode = formData.resolutionCode;
      }

      bulkUpdateMutation.mutate(updates);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="bg-white border-b border-gray-200 px-3 py-2 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Change State for {selectedCases.size} Case{selectedCases.size > 1 ? 's' : ''}
              </h2>
              <button
                onClick={() => setBulkActionType(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* State Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New State <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value, notes: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select State</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="awaiting_info">Awaiting Info</option>
                  <option value="on_hold">On Hold</option>
                  <option value="solution_proposed">Solution Proposed</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Resolution Code (for closed states) */}
              {(formData.state === 'closed' || formData.state === 'closed_complete' || formData.state === 'closed_incomplete') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resolution Code
                  </label>
                  <select
                    value={formData.resolutionCode}
                    onChange={(e) => setFormData({ ...formData, resolutionCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">Select Resolution Code</option>
                    <option value="solved_permanently">Solved Permanently</option>
                    <option value="solved_fixed_by_support">Solved - Fixed by Support</option>
                    <option value="solved_by_customer">Solved by Customer</option>
                    <option value="inconclusive_out_of_scope">Inconclusive - Out of Scope</option>
                    <option value="duplicate_issue">Duplicate Issue</option>
                    <option value="auto_resolved">Auto Resolved</option>
                  </select>
                </div>
              )}

              {/* State-specific Notes */}
              {formData.state && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.state === 'awaiting_info' && 'Information Required'}
                    {formData.state === 'on_hold' && 'On Hold Reason'}
                    {(formData.state === 'closed' || formData.state === 'closed_complete' || formData.state === 'closed_incomplete') && 'Resolution Notes'}
                    {!['awaiting_info', 'on_hold', 'closed', 'closed_complete', 'closed_incomplete'].includes(formData.state) && 'Notes (Optional)'}
                  </label>
                  <RichTextEditor
                    value={formData.notes}
                    onChange={(value) => setFormData({ ...formData, notes: value })}
                    placeholder={
                      formData.state === 'awaiting_info' ? 'Describe what information is needed...' :
                      formData.state === 'on_hold' ? 'Explain why these cases are on hold...' :
                      (formData.state === 'closed' || formData.state === 'closed_complete' || formData.state === 'closed_incomplete') ? 'Describe how the issues were resolved...' :
                      'Add notes about this state change...'
                    }
                    minHeight="120px"
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setBulkActionType(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={bulkUpdateMutation.isPending}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
              >
                {bulkUpdateMutation.isPending ? 'Updating...' : `Change State for ${selectedCases.size} Case${selectedCases.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Add Notes Modal Component
  function AddNotesModal() {
    const [formData, setFormData] = useState({
      noteType: 'internal',
      notes: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.notes.trim()) {
        setToast({
          message: 'Please enter notes',
          type: 'warning'
        });
        setTimeout(() => setToast(null), 5000);
        return;
      }

      const updates: any = {};
      const sanitizedNotes = sanitizeNoteInput(formData.notes);
      if (!hasActualTextContent(sanitizedNotes)) {
        setToast({
          message: 'Please enter notes',
          type: 'warning'
        });
        setTimeout(() => setToast(null), 5000);
        return;
      }
      if (formData.noteType === 'internal') {
        updates.internalNote = sanitizedNotes;
      } else {
        updates.publicNote = sanitizedNotes;
      }

      bulkUpdateMutation.mutate(updates);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="bg-white border-b border-gray-200 px-3 py-2 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Add Notes to {selectedCases.size} Case{selectedCases.size > 1 ? 's' : ''}
              </h2>
              <button
                onClick={() => setBulkActionType(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              {/* Note Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Note Visibility <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="noteType"
                      value="internal"
                      checked={formData.noteType === 'internal'}
                      onChange={(e) => setFormData({ ...formData, noteType: e.target.value })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Internal (Staff Only)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="noteType"
                      value="customer"
                      checked={formData.noteType === 'customer'}
                      onChange={(e) => setFormData({ ...formData, noteType: e.target.value })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Customer (Visible to Customer)</span>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  value={formData.notes}
                  onChange={(value) => setFormData({ ...formData, notes: value })}
                  placeholder={
                    formData.noteType === 'internal'
                      ? 'Add internal notes (visible to staff only)...'
                      : 'Add customer notes (visible to customers)...'
                  }
                  minHeight="150px"
                />
                <p className="mt-2 text-sm text-gray-500">
                  {formData.noteType === 'internal'
                    ? 'These notes will only be visible to internal staff members.'
                    : 'These notes will be visible to customers in the communications tab.'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setBulkActionType(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={bulkUpdateMutation.isPending}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
              >
                {bulkUpdateMutation.isPending ? 'Adding...' : `Add Notes to ${selectedCases.size} Case${selectedCases.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
};

export default CaseList;
